/**
 * FAQ copy — the owner's "Melio FAQ, copy and page structure" brief of
 * 14 September 2026, transcribed as written. Five groups carry the Roman
 * numerals; questions do not. Inline links use the legal renderer's
 * `[label](href)` form so the answers point at the Terms and the Privacy
 * Notice instead of repeating them.
 *
 * Decisions the brief leaves open are the owner's, not this file's: the
 * fee answer names the £480 introductory assessment (locked, 13 October 2026)
 * and leaves the monthly fee to the plan chosen, because several plans are
 * coming and the page must not go stale; the closing action writes to the
 * marketing mailbox (the brief asks for one purpose-specific address to be
 * agreed); questions about replacing a GP or prescribing are deliberately
 * absent. Clinical should approve the testing, abnormal-results and
 * urgent-care answers, and Compliance the commitment, fees and privacy copy.
 */

export const FAQ_META = {
  title: 'Questions, answered plainly · Melio',
  description:
    'How Melio works, what membership includes and what to expect from assessment, testing and follow-through.',
} as const;

export const FAQ_HERO = {
  eyebrow: 'Frequently asked',
  heading: 'Questions, answered plainly.',
  body: 'How Melio works, what membership includes and what to expect from assessment, testing and follow-through.',
} as const;

export interface FaqItem {
  id: string;
  question: string;
  /** Paragraphs. Inline `[label](href)` becomes a link. */
  answer: string[];
}

export interface FaqGroup {
  id: string;
  numeral: string;
  title: string;
  items: FaqItem[];
}

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: 'about',
    numeral: 'I',
    title: 'About Melio',
    items: [
      {
        id: 'what-is-melio',
        question: 'What is Melio?',
        answer: [
          'Melio is a clinician-led preventive health membership for adults who want their health understood and managed over time. We bring together your history, results, risks and priorities in one record, then turn them into a clear plan with named responsibility for follow-through.',
        ],
      },
      {
        id: 'why-melio',
        question: 'Why is it called Melio?',
        answer: [
          'Melio comes from the Latin verb meliorare: to improve or make better. The name reflects how we think about health: not as a dramatic transformation or one-off reset, but as steady improvement through better judgement, follow-through and time.',
        ],
      },
      {
        id: 'who-for',
        question: 'Who is Melio for?',
        answer: [
          'Melio is for adults aged 18 and over who want a more considered, continuous approach to their health. You may feel well and want to understand your risks, have results or concerns that need joining up, or simply want one trusted team to keep track of what matters over time. We assess suitability before membership begins.',
        ],
      },
      {
        id: 'different',
        question: 'How is Melio different from a health check or testing platform?',
        answer: [
          'Most health checks end with a report, while testing platforms can leave you to interpret the results yourself. Melio starts with your context, uses testing selectively and has a clinician review what the findings mean for you. The same team then carries the plan forward rather than handing the work back to you.',
        ],
      },
    ],
  },
  {
    id: 'starting',
    numeral: 'II',
    title: 'Starting with Melio',
    items: [
      {
        id: 'joining',
        question: 'What happens when I join?',
        answer: [
          'You begin with a structured introduction covering your health history, family history, priorities, medications and previous care. A clinician reviews that information alongside any useful existing records, then sets out what should be investigated now, what can wait and what should be followed over time.',
        ],
      },
      {
        id: 'previous-results',
        question: 'Can Melio use my previous results and records?',
        answer: [
          'Yes. You can add relevant blood results, clinic letters, medication information, imaging reports and other health records so that your plan builds on what is already known. Useful wearable data can also provide context, but it does not replace clinical assessment.',
        ],
      },
      {
        id: 'particular-concern',
        question: 'Do I need to have a particular health concern?',
        answer: [
          'No. Some members join with a specific question, while others want a reliable baseline and someone to manage the longer view. If you have new or urgent symptoms, you should use your GP, NHS 111 or emergency services rather than wait for a Melio assessment.',
        ],
      },
    ],
  },
  {
    id: 'testing',
    numeral: 'III',
    title: 'Assessments and testing',
    items: [
      {
        id: 'tests-chosen',
        question: 'How are my tests chosen?',
        answer: [
          'Your history, family history, previous results, current concerns and goals help determine what is useful to assess. Melio does not order the largest possible panel by default. Your clinician considers what could change understanding or next steps, and what is unlikely to add value.',
        ],
      },
      {
        id: 'request-test',
        question: 'Can I request a particular test?',
        answer: [
          'Yes. You can explore [available profiles](/assessments) and ask about a test that matters to you. Your clinician will consider whether it is suitable, explain any limitations and suggest a different or more focused option where that would answer the question better.',
        ],
      },
      {
        id: 'additional-testing',
        question: 'Can my clinician recommend additional testing?',
        answer: [
          'Yes. Your clinician may recommend a focused profile, repeat test, confirmatory test or bespoke combination where your history or findings make it useful. Nothing additional is arranged without explaining the reason, practical requirements and cost first.',
        ],
      },
      {
        id: 'where-testing',
        question: 'Where does testing take place?',
        answer: [
          'Melio arranges testing through external collection and laboratory partners. The available collection method and location depend on the test, your location and sample requirements. We confirm the practical options before anything is booked.',
        ],
      },
      {
        id: 'how-often',
        question: 'How often will I be tested?',
        answer: [
          'Your plan is reviewed quarterly, but testing does not follow the same fixed schedule for everyone. Some measures are useful as a baseline, some need repeating to show a trend and others should only be revisited when your circumstances or clinical picture change.',
        ],
      },
    ],
  },
  {
    id: 'results',
    numeral: 'IV',
    title: 'Results and next steps',
    items: [
      {
        id: 'who-reviews',
        question: 'Who reviews my results?',
        answer: [
          'A clinician reviews and approves every member-specific finding before it is finalised. Results are considered alongside your history, previous measurements, medications and the rest of your record rather than read as isolated numbers.',
        ],
      },
      {
        id: 'when-results',
        question: 'When will I receive my results?',
        answer: [
          'Turnaround times vary by test and some specialist results take longer than routine profiles. We explain the expected timing when testing is arranged and let you know when the relevant results are ready for clinical review.',
        ],
      },
      {
        id: 'explain-results',
        question: 'Will someone explain what my results mean?',
        answer: [
          'Yes. Results are not simply placed in a portal for you to decode alone. Melio explains what is meaningful, what may need confirmation, what can be watched and what should happen next.',
        ],
      },
      {
        id: 'abnormal',
        question: 'What happens if a result is abnormal?',
        answer: [
          'A clinician assesses its significance and urgency. Depending on the finding, the next step may be clarification, repeat or confirmatory testing, monitoring, a change to your plan or onward care from your GP or a specialist. A critical result is escalated promptly rather than held for the next scheduled review.',
        ],
      },
      {
        id: 'share-gp',
        question: 'Can I share my results with my GP or another clinician?',
        answer: [
          'Yes. Your Melio record is designed to support continuity with the rest of your care. With your consent, relevant results and clinical summaries can be shared with your GP or specialist so they have the context needed to act.',
        ],
      },
    ],
  },
  {
    id: 'membership',
    numeral: 'V',
    title: 'Membership, fees and privacy',
    items: [
      {
        id: 'cost',
        question: 'What does membership cost and include?',
        answer: [
          'Membership begins with a one-time £480 introductory assessment. The monthly fee depends on the plan you choose and is set out before you join. Every plan includes a named care team, quarterly clinician-reviewed reports, secure messaging between reviews and a continuous health record held over time.',
        ],
      },
      {
        id: 'extras',
        question: 'Are tests and specialist appointments included?',
        answer: [
          'Additional tests, imaging, specialist consultations and other services may be charged separately unless they are expressly included in the package shown when you join. We explain the purpose and price before placing an order or making a booking.',
        ],
      },
      {
        id: 'commitment',
        question: 'What is the membership commitment?',
        answer: [
          'Membership runs for 12 months and can be paid monthly. The applicable cancellation, rescheduling and refund terms are shown before purchase and set out in the [Membership Terms](/terms).',
        ],
      },
      {
        id: 'privacy',
        question: 'How does Melio protect my information?',
        answer: [
          "Health and genetic information receives additional protection. Melio uses authenticated access, role-based permissions, encryption, audit logs and supplier controls, with clinical access limited to people who need the information for your care. Melio Health Solutions Ltd is registered with the Information Commissioner's Office under reference ZC153092. Our [Privacy Notice](/privacy) explains how information is used, retained and shared.",
        ],
      },
      {
        id: 'urgent',
        question: 'What should I do if I need urgent medical help?',
        answer: [
          'Do not wait for a Melio message or appointment. Call 999 or attend A&E for an emergency. Use NHS 111 or your GP for urgent advice that cannot wait. Melio does not provide emergency or out-of-hours care.',
        ],
      },
    ],
  },
];

export const FAQ_CLOSING = {
  heading: 'Is there something we have not answered?',
  body: 'Write to the practice and a member of the team will help.',
  primary: 'Write to the practice',
  secondary: 'Join the waitlist',
} as const;
